import React, { useState, useEffect, useCallback } from 'react';
import { Brain, RefreshCw, Plus, Code, Database, Globe, Palette, Shield, Cpu, StickyNote, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';

const CATEGORY_ICONS = {
  frontend: <Code className="w-3.5 h-3.5" />,
  backend: <Cpu className="w-3.5 h-3.5" />,
  database: <Database className="w-3.5 h-3.5" />,
  styling: <Palette className="w-3.5 h-3.5" />,
  deployment: <Globe className="w-3.5 h-3.5" />,
  auth: <Shield className="w-3.5 h-3.5" />,
  ai_model: <Brain className="w-3.5 h-3.5" />,
};

const CATEGORY_COLORS = {
  frontend: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  backend: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  database: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  styling: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  deployment: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
  auth: 'bg-red-500/20 text-red-400 border-red-500/30',
  ai_model: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
};

const AIMemoryPanel = ({ project }) => {
  const [memory, setMemory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [newNote, setNewNote] = useState('');

  const fetchMemory = useCallback(async () => {
    if (!project?.id) return;
    try {
      const res = await api.get(`/projects/${project.id}/memory`);
      setMemory(res.data);
    } catch { setMemory(null); }
    setLoading(false);
  }, [project?.id]);

  useEffect(() => { fetchMemory(); }, [fetchMemory]);

  const handleRefresh = async () => {
    if (!project?.id) return;
    setRefreshing(true);
    try {
      const res = await api.post(`/projects/${project.id}/memory/refresh`);
      setMemory(res.data);
      toast({ title: 'Memory Refreshed', description: `Scanned ${res.data.message_count} messages` });
    } catch {
      toast({ title: 'Error', description: 'Failed to refresh memory', variant: 'destructive' });
    }
    setRefreshing(false);
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !project?.id) return;
    try {
      await api.post(`/projects/${project.id}/memory/note`, { note: newNote.trim() });
      setNewNote('');
      fetchMemory();
      toast({ title: 'Note Added' });
    } catch {
      toast({ title: 'Error', description: 'Failed to add note', variant: 'destructive' });
    }
  };

  const techStack = memory?.tech_stack || {};
  const decisions = memory?.decisions || [];
  const codeLangs = memory?.code_languages || {};
  const customNotes = memory?.custom_notes || [];
  const hasTechStack = Object.keys(techStack).length > 0;

  return (
    <div className="h-full flex flex-col bg-background overflow-y-auto">
      {/* Header */}
      <div className="border-b border-border p-5 bg-card">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center">
              <Brain className="w-5 h-5 text-foreground" />
            </div>
            <div>
              <h2 className="text-foreground font-semibold text-lg" data-testid="memory-title">AI Memory</h2>
              <p className="text-xs text-muted-foreground">Context the AI remembers about this project</p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={handleRefresh} disabled={refreshing}
            className="border-input text-muted-foreground hover:text-foreground hover:bg-accent gap-1.5"
            data-testid="refresh-memory-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        {memory?.message_count !== undefined && (
          <p className="text-xs text-muted-foreground/70">Based on {memory.message_count} messages &middot; Updated {memory.last_updated ? new Date(memory.last_updated).toLocaleDateString() : 'never'}</p>
        )}
      </div>

      <div className="flex-1 p-5 space-y-4">
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="animate-pulse h-16 bg-muted rounded-lg" />)}
          </div>
        ) : (
          <>
            {/* Tech Stack */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <h3 className="text-foreground font-semibold text-sm mb-3">Tech Stack</h3>
                {hasTechStack ? (
                  <div className="space-y-2">
                    {Object.entries(techStack).map(([category, items]) => (
                      <div key={category} className="flex items-start gap-2">
                        <Badge className={`${CATEGORY_COLORS[category] || 'bg-slate-500/20 text-muted-foreground'} gap-1 text-xs shrink-0`}>
                          {CATEGORY_ICONS[category]} {category}
                        </Badge>
                        <div className="flex flex-wrap gap-1">
                          {items.map(item => (
                            <span key={item} className="px-2 py-0.5 text-xs rounded bg-secondary text-foreground/80 border border-input" data-testid={`tech-${item}`}>
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No tech stack detected yet. Start chatting to build context.</p>
                )}
              </CardContent>
            </Card>

            {/* Code Languages */}
            {Object.keys(codeLangs).length > 0 && (
              <Card className="bg-card border-border">
                <CardContent className="p-4">
                  <h3 className="text-foreground font-semibold text-sm mb-3">Code Languages Used</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(codeLangs).map(([lang, count]) => (
                      <Badge key={lang} className="bg-secondary text-foreground/80 border-input gap-1" data-testid={`lang-${lang}`}>
                        <Code className="w-3 h-3" /> {lang} <span className="text-muted-foreground">({count})</span>
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Decisions */}
            {decisions.length > 0 && (
              <Card className="bg-card border-border">
                <CardContent className="p-4">
                  <h3 className="text-foreground font-semibold text-sm mb-3">Key Decisions</h3>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto pr-1">
                    {decisions.map((d, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <span className="text-blue-400 shrink-0 mt-0.5">-</span>
                        <span className="text-muted-foreground">{d}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Custom Notes */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <h3 className="text-foreground font-semibold text-sm mb-3 flex items-center gap-1.5"><StickyNote className="w-4 h-4 text-amber-400" /> Notes</h3>
                <div className="flex gap-2 mb-3">
                  <Input
                    value={newNote} onChange={e => setNewNote(e.target.value)}
                    placeholder="Add a note for the AI to remember..."
                    className="bg-background border-input text-foreground placeholder:text-muted-foreground text-sm"
                    onKeyDown={e => e.key === 'Enter' && handleAddNote()}
                    data-testid="memory-note-input"
                  />
                  <Button size="sm" onClick={handleAddNote} className="bg-blue-600 hover:bg-blue-500 shrink-0" data-testid="add-note-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {customNotes.length > 0 ? (
                  <div className="space-y-2">
                    {customNotes.map((note, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 bg-muted rounded-lg border border-input" data-testid={`note-${i}`}>
                        <StickyNote className="w-3 h-3 text-amber-400 shrink-0 mt-0.5" />
                        <span className="text-xs text-foreground/80 flex-1">{note.text}</span>
                        <span className="text-xs text-muted-foreground/70 shrink-0">{new Date(note.added_at).toLocaleDateString('en-US', {month:'short',day:'numeric'})}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground/70">No notes yet. Add context the AI should remember.</p>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default AIMemoryPanel;
